/**
 * Виджет «Крупнейшие проекты и проекты с риском» (FR-8): переключатель
 * между перечнями «Крупнейшие» (топ-5 по сумме) и «Риск срыва освоения»
 * (незавершённые с годовым освоением < 60%, по возрастанию %); строка —
 * сумма, % освоения и цветовая метка (FR-8.3); клик открывает карточку.
 * Границы цветовой индикации — условные (FR-8.2, ТРЕБУЕТ УТОЧНЕНИЯ).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fmt, riskLevel, yearDevelopmentPct, type RiskLevel } from './format';
import { SegToggle } from './SegToggle';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { cn } from '../../../lib/cn';
import type { InfrastructureProject } from '../../../types/projects';

type Mode = 'top' | 'risk';

const BADGE: Record<RiskLevel, { dot: string; text: string }> = {
  ok: { dot: 'bg-brand-500', text: 'text-brand-700' },
  warning: { dot: 'bg-status-warning', text: 'text-text' },
  serious: { dot: 'bg-status-danger', text: 'text-text' },
  critical: { dot: 'bg-status-critical', text: 'text-status-critical' },
};

const RiskBadge = ({ pct }: { pct: number }) => {
  const level = riskLevel(pct);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 justify-self-end text-sm font-semibold tabular-nums',
        BADGE[level].text,
      )}
    >
      <span
        className={cn('size-2 rounded-full', BADGE[level].dot)}
        aria-hidden="true"
      />
      {Math.round(pct)}%
    </span>
  );
};

interface TopRiskWidgetProps {
  projects: InfrastructureProject[];
  onOpen: (project: InfrastructureProject) => void;
}

export const TopRiskWidget = ({ projects, onOpen }: TopRiskWidgetProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('top');

  const list =
    mode === 'top'
      ? [...projects]
          .sort((a, b) => (b.sumMln ?? 0) - (a.sumMln ?? 0))
          .slice(0, 5)
      : projects
          .filter(
            (p) => p.status !== 'done' && yearDevelopmentPct(p) < 60,
          )
          .sort((a, b) => yearDevelopmentPct(a) - yearDevelopmentPct(b));

  return (
    <Card className="shadow-card" data-testid="projects-top-risk">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>{t('projects.infrastructure.topRisk.title')}</CardTitle>
        <SegToggle<Mode>
          ariaLabel={t('projects.infrastructure.topRisk.title')}
          options={[
            { value: 'top', label: t('projects.infrastructure.topRisk.top') },
            { value: 'risk', label: t('projects.infrastructure.topRisk.risk') },
          ]}
          value={mode}
          onChange={setMode}
          testId="projects-top-risk-mode"
        />
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <ul className="min-w-[40rem]" data-testid="projects-top-risk-list">
            {list.map((project, index) => {
              const pct = yearDevelopmentPct(project);
              return (
                <li key={project.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(project)}
                    className="grid w-full grid-cols-[1.5rem_minmax(0,1fr)_9rem_7rem_5rem] items-center gap-2.5 border-b border-surface-border px-1 py-2.5 text-left hover:bg-brand-50/60"
                    data-testid={`projects-top-risk-row-${project.id}`}
                  >
                    <span className="text-center text-xs tabular-nums text-text-faint">
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold">
                        {project.name}
                      </span>
                      <span className="block truncate text-xs text-text-muted">
                        {[project.territory, project.abp]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block text-sm font-semibold tabular-nums">
                        {project.sumMln != null
                          ? `${fmt(project.sumMln)} ${t('projects.units.mlnTg')}`
                          : '—'}
                      </span>
                      <span className="block text-[11px] text-text-faint">
                        {t('projects.infrastructure.topRisk.sum')}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block text-sm tabular-nums">
                        {Math.round(pct)}%
                      </span>
                      <span className="block text-[11px] text-text-faint">
                        {t('projects.infrastructure.topRisk.development')}
                      </span>
                    </span>
                    <RiskBadge pct={pct} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        {/* FR-8.2: границы уровней условные — до уточнения PO/разработкой. */}
        <p className="mt-3 text-xs text-text-faint">
          {t('projects.infrastructure.topRisk.note')}
        </p>
      </CardContent>
    </Card>
  );
};
